/* Bundle builder, split out of base.js: it is ~550 lines that only the
   /pages/bundle-builder template needs. Loaded from sections/bundle-builder.liquid
   after base.js, which still provides shared helpers such as togglePageScroll. */

class BundleBuilder extends HTMLElement {
  constructor() {
    super();

    this.isLoading = false;
    this.cookieName = "bundle_builder";
    this.cookieMaxAge = 172800;
    this.mobileBreakpoint = 989;

    this.selected = [];
    this.spray = null;
  }

  connectedCallback() {
    const dataElement = this.querySelector("[data-bundle-data]");
    if (!dataElement) return;

    this.data = JSON.parse(dataElement.textContent);
    this.strings = this.data.strings;
    this.tiers = this.data.tiers.slice().sort((a, b) => a.count - b.count);
    this.sprayIds = Object.keys(this.data.sprays);

    this.restoreState();
    this.initClickEvents();
    this.initScrollSpy();
    this.render();
  }

  initScrollSpy() {
    this.jump = this.querySelector("[data-jump]");
    this.chipList = this.querySelector(".bundle-builder__chips");
    this.chips = Array.from(this.querySelectorAll("[data-chip]"));
    this.categories = this.chips.map(chip => document.getElementById(chip.dataset.target));
    this.activeChip = null;

    window.addEventListener("scroll", () => {
      if (this.spyFrame) return;
      this.spyFrame = requestAnimationFrame(() => {
        this.spyFrame = null;
        this.updateActiveChip();
      });
    }, { passive: true });

    this.updateActiveChip();
  }

  updateActiveChip() {
    const line = this.jump.getBoundingClientRect().bottom + 32;
    let active = -1;

    this.categories.forEach((category, index) => {
      if (category && category.getBoundingClientRect().top <= line) active = index;
    });

    const lastCategory = this.categories[this.categories.length - 1];
    if (lastCategory && lastCategory.getBoundingClientRect().bottom < line) active = -1;

    if (active === this.activeChip) return;
    this.activeChip = active;

    this.chips.forEach((chip, index) => chip.classList.toggle("active", index === active));

    const chip = this.chips[active];
    if (!chip || this.chipList.scrollWidth <= this.chipList.clientWidth) return;

    const listRect = this.chipList.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    this.chipList.scrollTo({
      left: this.chipList.scrollLeft + chipRect.left - listRect.left - (listRect.width - chipRect.width) / 2,
      behavior: "smooth"
    });
  }

  initClickEvents() {
    this.addEventListener("click", (event) => {
      const trigger = event.target.closest("[data-action]");
      if (!trigger) return;

      switch (trigger.dataset.action) {
        case "toggle-product":
          this.toggleProduct(trigger.closest("[data-card]").dataset.variant);
          break;
        case "remove-item":
          this.toggleProduct(trigger.dataset.variant, true);
          break;
        case "jump-to":
          this.scrollToCategory(trigger.dataset.target);
          break;
        case "toggle-spray-chooser":
          this.toggleSprayChooser();
          break;
        case "select-spray":
          this.spray = trigger.dataset.sprayVariant;
          this.querySelectorAll("[data-spray-chooser]").forEach(chooser => chooser.classList.remove("open"));
          this.saveState();
          this.render();
          break;
        case "open-bundle-sheet":
          this.openSheet("bundle");
          break;
        case "close-sheet":
          this.closeSheet();
          break;
        case "add-bundle":
          this.addBundle();
          break;
      }
    });
  }

  /* State */

  toggleProduct(variant, forceRemove = false) {
    if (!variant || !this.data.products[variant]) return;

    if (this.selected.includes(variant) || forceRemove) {
      this.selected = this.selected.filter(id => id !== variant);
    } else {
      this.selected.push(variant);
    }

    this.saveState();
    this.render();
  }

  restoreState() {
    const cookie = document.cookie.split("; ").find(row => row.startsWith(this.cookieName + "="));
    if (!cookie) return;

    try {
      const state = JSON.parse(decodeURIComponent(cookie.split("=").slice(1).join("=")));
      this.selected = (state.items || []).filter(id => this.data.products[id]);
      this.spray = this.data.sprays[state.spray] ? state.spray : null;
    } catch (error) {
      this.selected = [];
      this.spray = null;
    }
  }

  saveState() {
    const value = encodeURIComponent(JSON.stringify({ items: this.selected, spray: this.spray }));
    document.cookie = `${this.cookieName}=${value}; path=/; max-age=${this.cookieMaxAge}; SameSite=Lax`;
  }

  /* Tiers */

  getCurrentTier() {
    return this.tiers.reduce((tier, item) => (this.selected.length >= item.count ? item : tier), null);
  }

  getNextTier() {
    return this.tiers.find(item => this.selected.length < item.count) || null;
  }

  getProgressRatio() {
    const count = this.selected.length;
    const lastIndex = this.tiers.length - 1;

    if (lastIndex <= 0) return count >= this.tiers[0].count ? 1 : 0;

    let reachedIndex = -1;
    this.tiers.forEach((tier, index) => {
      if (count >= tier.count) reachedIndex = index;
    });

    if (reachedIndex < 0) return 0;
    if (reachedIndex >= lastIndex) return 1;

    const span = this.tiers[reachedIndex + 1].count - this.tiers[reachedIndex].count;
    const partial = span > 0 ? (count - this.tiers[reachedIndex].count) / span : 0;

    return (reachedIndex + partial) / lastIndex;
  }

  /* Formatting */

  fill(template, vars = {}) {
    let result = template || "";

    if (vars.count !== undefined) {
      result = result
        .replace(/\[count\]/g, vars.count)
        .replace(/\[unit\]/g, vars.count === 1 ? this.strings.unitOne : this.strings.unitOther);
    }
    if (vars.discount !== undefined) result = result.replace(/\[discount\]/g, vars.discount);
    if (vars.price !== undefined) result = result.replace(/\[price\]/g, vars.price);
    if (vars.label !== undefined) result = result.replace(/\[label\]/g, vars.label);
    if (vars.index !== undefined) result = result.replace(/\[index\]/g, vars.index);
    if (vars.title !== undefined) result = result.replace(/\[title\]/g, vars.title);

    return result;
  }

  formatMoney(cents, trimZeros = false) {
    const format = this.data.moneyFormat || "€{{amount_with_comma_separator}}";
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = placeholder ? placeholder[1] : "amount";
    const useComma = key.indexOf("comma_separator") !== -1;
    const noDecimals = key.indexOf("no_decimals") !== -1 || (trimZeros && cents % 100 === 0);

    const parts = (cents / 100).toFixed(noDecimals ? 0 : 2).split(".");
    const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, useComma ? "." : ",");
    const amount = parts[1] ? integer + (useComma ? "," : ".") + parts[1] : integer;

    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  /* Render */

  render() {
    const count = this.selected.length;
    const currentTier = this.getCurrentTier();
    const nextTier = this.getNextTier();

    if (!currentTier) {
      this.spray = null;
    } else if (!this.spray && this.sprayIds.length > 0) {
      this.spray = this.sprayIds[0];
    }

    this.renderCards();
    this.renderMeter(count);
    this.renderNudge(count, nextTier);
    this.renderSpray(Boolean(currentTier));
    this.renderItems(count, nextTier);
    this.renderTotals(count, currentTier);
    this.renderCta(count, currentTier, nextTier);
  }

  renderCards() {
    this.querySelectorAll("[data-card]").forEach(card => {
      card.classList.toggle("selected", this.selected.includes(card.dataset.variant));
    });
  }

  renderMeter(count) {
    const ratio = this.getProgressRatio();

    this.querySelectorAll("[data-meter-fill]").forEach(fill => {
      fill.style.width = `calc((100% - 80px) * ${ratio})`;
    });

    this.querySelectorAll("[data-meter-point]").forEach(point => {
      const target = parseInt(point.dataset.meterPoint);
      point.classList.toggle("reached", count >= target);
      point.classList.toggle("next", count < target && target === this.getNextTier()?.count);
    });
  }

  renderNudge(count, nextTier) {
    const isMax = !nextTier;

    this.querySelectorAll("[data-nudge]").forEach(nudge => {
      nudge.classList.toggle("completed", isMax);
      const strong = nudge.querySelector("[data-nudge-strong]");
      const rest = nudge.querySelector("[data-nudge-rest]");

      if (isMax) {
        strong.textContent = this.strings.maxReached;
        rest.textContent = "";
        return;
      }

      const remaining = nextTier.count - count;
      strong.textContent = this.fill(
        count === 0 ? this.strings.nudgeChoose : this.strings.nudgeMore,
        { count: remaining }
      );
      rest.textContent = this.fill(
        count === 0 ? this.strings.nudgeRestFirst : this.strings.nudgeRestMore,
        { discount: nextTier.discount }
      );
    });
  }

  renderSpray(unlocked) {
    const spray = this.spray ? this.data.sprays[this.spray] : null;

    this.querySelectorAll("[data-spray-block]").forEach(block => {
      block.classList.toggle("unlocked", unlocked);

      const name = block.querySelector("[data-spray-name]");
      const sub = block.querySelector("[data-spray-sub]");
      const thumb = block.querySelector("[data-spray-thumb] img");

      if (unlocked && spray) {
        name.textContent = this.fill(this.strings.sprayName, { title: spray.title });
        sub.innerHTML = `<span class="bundle-spray__price-free">${this.formatMoney(0)}</span><span class="bundle-spray__price-compare">${this.formatMoney(spray.price, true)}</span>`;
        if (thumb && spray.image) {
          thumb.src = spray.image;
          thumb.srcset = spray.image;
          thumb.alt = spray.title;
        }
      } else {
        name.textContent = this.strings.sprayLocked;
        sub.textContent = this.strings.sprayUnlockedFrom;
        if (!unlocked) block.querySelectorAll("[data-spray-chooser]").forEach(chooser => chooser.classList.remove("open"));
      }
    });

    this.querySelectorAll("[data-spray-variant]").forEach(tile => {
      tile.classList.toggle("selected", tile.dataset.sprayVariant === this.spray);
    });
  }

  renderItems(count, nextTier) {
    const items = this.selected.map(id => {
      const product = this.data.products[id];
      const image = product.image ? `<img src="${product.image}" alt="" loading="lazy">` : "";

      return `<div class="bundle-item">
        <span class="bundle-item__thumb">${image}</span>
        <p class="bundle-item__title">${product.title}</p>
        <p class="bundle-item__price">${this.formatMoney(product.price, true)}</p>
        <button type="button" class="bundle-item__remove" data-action="remove-item" data-variant="${id}" aria-label="${this.strings.remove}">✕</button>
      </div>`;
    });

    if (nextTier) {
      const slots = [];
      for (let slot = count + 1; slot <= nextTier.count; slot++) {
        const text = this.fill(
          count === 0 ? this.strings.emptyFirst : this.strings.emptyMore,
          { index: slot, discount: nextTier.discount }
        );
        slots.push(`<div class="bundle-slot">+&nbsp; ${text}</div>`);
      }
      items.push(`<div class="bundle-summary__slots">${slots.join("")}</div>`);
    }

    this.querySelectorAll("[data-items]").forEach(list => (list.innerHTML = items.join("")));
  }

  renderTotals(count, currentTier) {
    const subtotal = this.selected.reduce((sum, id) => sum + this.data.products[id].price, 0);
    const discount = currentTier ? Math.round((subtotal * currentTier.discount) / 100) : 0;
    const spray = this.spray ? this.data.sprays[this.spray] : null;
    const sprayPrice = spray ? spray.price : 0;
    const total = subtotal - discount;
    const savings = discount + sprayPrice;

    this.querySelectorAll("[data-summary]").forEach(summary => {
      const countLabel = summary.querySelector("[data-summary-count]");
      if (countLabel) countLabel.textContent = this.fill("[count] [unit]", { count: count });

      const subtotalLabel = summary.querySelector("[data-total-subtotal-label]");
      if (!subtotalLabel) return;

      subtotalLabel.textContent = count > 0
        ? this.fill(this.strings.subtotalCount, { count: count })
        : this.strings.subtotal;
      summary.querySelector("[data-total-subtotal]").textContent = this.formatMoney(subtotal);

      const discountRow = summary.querySelector("[data-discount-row]");
      discountRow.classList.toggle("active", Boolean(currentTier));
      summary.querySelector("[data-total-discount-label]").textContent = currentTier
        ? this.fill(this.strings.bundleDiscountValue, { discount: currentTier.discount })
        : this.strings.bundleDiscount;
      summary.querySelector("[data-total-discount]").textContent = currentTier
        ? "−" + this.formatMoney(discount)
        : this.strings.empty;

      const sprayRow = summary.querySelector("[data-spray-row]");
      sprayRow.classList.toggle("active", Boolean(spray));
      summary.querySelector("[data-total-spray-label]").textContent = spray
        ? this.fill(this.strings.freeSprayValue, { price: this.formatMoney(sprayPrice, true) })
        : this.strings.freeSpray;
      summary.querySelector("[data-total-spray]").textContent = spray
        ? this.formatMoney(0)
        : this.strings.empty;

      summary.querySelector("[data-total-grand]").textContent = this.formatMoney(total);

      const savingsLabel = summary.querySelector("[data-savings]");
      savingsLabel.hidden = savings <= 0;
      savingsLabel.textContent = this.fill(this.strings.savings, { price: this.formatMoney(savings) });
    });

    this.querySelectorAll("[data-sticky-price]").forEach(el => (el.textContent = this.formatMoney(total)));
    this.querySelectorAll("[data-sticky-count]").forEach(el => {
      el.textContent = this.fill("[count] [unit]", { count: count });
    });
  }

  renderCta(count, currentTier, nextTier) {
    const remaining = nextTier ? nextTier.count - count : 0;
    const lockedLabel = this.fill(this.strings.ctaLocked, { count: remaining });
    const activeLabel = this.fill(this.strings.ctaActive, { count: count, label: this.strings.ctaLabel });

    this.querySelectorAll("[data-cta]").forEach(cta => {
      cta.disabled = !currentTier;
      cta.querySelector("[data-cta-label]").textContent = currentTier ? activeLabel : lockedLabel;
    });

    const maxCount = this.tiers[this.tiers.length - 1].count;
    const stickyRatio = nextTier ? count / nextTier.count : 1;

    this.querySelectorAll("[data-sticky-nudge]").forEach(el => {
      el.textContent = nextTier
        ? this.fill(
            count === 0 ? this.strings.stickyNudgeChoose : this.strings.stickyNudgeMore,
            { count: remaining, discount: nextTier.discount }
          )
        : this.strings.maxReached;
    });

    this.querySelectorAll("[data-sticky-counter]").forEach(el => {
      el.textContent = `${count} / ${maxCount}`;
    });

    this.querySelectorAll("[data-sticky-fill]").forEach(el => (el.style.width = stickyRatio * 100 + "%"));

    this.querySelectorAll("[data-sticky-cta]").forEach(cta => {
      cta.disabled = !currentTier;
      cta.querySelector("[data-sticky-cta-label]").textContent = currentTier
        ? this.strings.stickyCtaOpen
        : lockedLabel;
    });
  }

  scrollToCategory(id) {
    const target = document.getElementById(id);
    if (!target) return;

    const rootStyle = getComputedStyle(document.documentElement);
    const announcementHeight = parseFloat(rootStyle.getPropertyValue("--announcement-height")) || 0;
    const headerHeight = parseFloat(rootStyle.getPropertyValue("--header-height")) || 0;
    const jumpHeight = this.querySelector("[data-jump]")?.offsetHeight || 0;
    const targetTop = target.getBoundingClientRect().top + window.scrollY;

    // The sticky header hides while scrolling down and reappears while scrolling up
    const headerOffset = targetTop < window.scrollY ? headerHeight : 0;

    window.scrollTo({
      top: targetTop - announcementHeight - headerOffset - jumpHeight - 24,
      behavior: "smooth"
    });
  }

  /* Sheets */

  toggleSprayChooser() {
    if (window.innerWidth <= this.mobileBreakpoint) {
      this.openSheet("spray");
      return;
    }

    this.querySelectorAll("[data-spray-chooser]").forEach(chooser => {
      const isOpen = chooser.classList.toggle("open");
      if (!isOpen) return;

      chooser.addEventListener("transitionend", () => {
        const area = chooser.closest("[data-scroll-area]");
        if (!area) return;

        const overflow = chooser.getBoundingClientRect().bottom - area.getBoundingClientRect().bottom;
        if (overflow > 0) area.scrollBy({ top: overflow + 16, behavior: "smooth" });
      }, { once: true });
    });
  }

  openSheet(name) {
    const sheets = this.querySelector("[data-sheets]");
    if (!sheets) return;

    sheets.querySelectorAll("[data-sheet]").forEach(sheet => {
      sheet.classList.toggle("active", sheet.dataset.sheet === name);
    });

    sheets.classList.add("open");
    document.body.classList.add("bundle-sheet-open");
  }

  closeSheet() {
    const sheets = this.querySelector("[data-sheets]");
    if (!sheets) return;

    sheets.classList.remove("open");
    sheets.querySelectorAll("[data-sheet]").forEach(sheet => sheet.classList.remove("active"));
    document.body.classList.remove("bundle-sheet-open");
  }

  /* Cart */

  createBundleId() {
    return "b-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
  }

  addBundle() {
    const currentTier = this.getCurrentTier();
    if (!currentTier || this.isLoading) return;

    this.toggleLoading();

    // The discount function groups cart lines by _bundle_id and only discounts
    // the line marked with _bundle_gift. Both properties start with "_" so they
    // stay hidden from the customer in cart and checkout.
    const bundleId = this.createBundleId();

    const items = this.selected.map(id => ({
      id: parseInt(id),
      quantity: 1,
      properties: { _bundle_id: bundleId }
    }));

    if (this.spray) {
      items.push({
        id: parseInt(this.spray),
        quantity: 1,
        properties: { _bundle_id: bundleId, _bundle_gift: "true" }
      });
    }

    const root = window.Shopify.routes.root;

    fetch(root + 'cart/clear.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }))
      .then(response => response.json())
      .then(response => {
        if (!response.items) {
          throw new Error(response.description);
        }

        let checkoutUrl = root + 'checkout';
        if (currentTier.code) checkoutUrl += '?discount=' + encodeURIComponent(currentTier.code);

        window.location.href = checkoutUrl;
      })
      .catch(error => {
        console.error(error);
        this.toggleLoading();
      });
  }

  toggleLoading() {
    this.isLoading = !this.isLoading;
    this.classList.toggle("loading", this.isLoading);
  }
}

if (document.querySelector("bundle-builder") && !customElements.get("bundle-builder")) {
  customElements.define("bundle-builder", BundleBuilder);
}
